const QUESTIONNAIRE_ANSWERABLE_TYPES = new Set([
  "display",
  "boolean",
  "decimal",
  "integer",
  "date",
  "dateTime",
  "time",
  "string",
  "text",
  "url",
  "choice",
  "open-choice",
  "attachment",
  "reference",
  "quantity"
]);

/**
 * BCNC Member Submission (Portal) 2025 A1C image upload.
 * The question is nested in a group, so it only renders after the page items are flattened.
 */
export const PORTAL_A1C_IMAGE_UPLOAD_LINK_ID = "PAYOR-A1C-IMG-ATTACHMENT";

/**
 * All A1C image upload linkIds — includes the optional second upload slot.
 */
export const PORTAL_A1C_IMAGE_UPLOAD_LINK_IDS = [
  PORTAL_A1C_IMAGE_UPLOAD_LINK_ID,
  "PAYOR-A1C-IMG-ATTACHMENT-2",
];

/**
 * True when a questionnaire node is a structural group rather than an answerable question.
 */
export function isQuestionnaireGroupNode(node: { type?: string; item?: unknown[] } | null | undefined): boolean {
  if (!node?.item || !Array.isArray(node.item) || node.item.length === 0) return false;
  if (node.type === "group") return true;
  if (node.type && QUESTIONNAIRE_ANSWERABLE_TYPES.has(node.type)) return false;
  return true;
}

/**
 * Flattens a page's item tree into answerable questions, skipping group containers.
 *
 * @remarks
 * Member Submission (Portal) 2025 nests the A1C image upload inside a group. Care Betty
 * flattens that tree before rendering; without this, the upload control never mounts.
 *
 * When a group carries `enableWhen` and a child does not, the group's constraint is
 * inherited so the child stays hidden until the parent condition is met, even after
 * the group node is dropped from the flattened list.
 */
export function flattenQuestionnaireItems(
  items: any[] | undefined,
  parentEnableWhen?: any[],
  parentEnableBehavior?: string
): any[] {
  const acc: any[] = [];
  for (const node of items ?? []) {
    if (!node) continue;
    if (isQuestionnaireGroupNode(node)) {
      // Propagate this group's enableWhen down; prefer the group's own over any ancestor's.
      const inheritedEnableWhen = node.enableWhen ?? parentEnableWhen;
      const inheritedEnableBehavior = node.enableBehavior ?? parentEnableBehavior;
      acc.push(...flattenQuestionnaireItems(node.item, inheritedEnableWhen, inheritedEnableBehavior));
    } else {
      // Copy the parent's enableWhen onto a shallow clone only when the child has none.
      if (parentEnableWhen && !node.enableWhen) {
        acc.push({ ...node, enableWhen: parentEnableWhen, enableBehavior: parentEnableBehavior });
      } else {
        acc.push(node);
      }
    }
  }
  return acc;
}

/**
 * One flattened question list per top-level questionnaire page.
 */
export function getQuestionnairePageItems(questionnaire: { item?: any[] } | undefined): any[][] {
  return (questionnaire?.item ?? []).map((page) => flattenQuestionnaireItems(page?.item));
}

/**
 * Payer portal keeps other attachment questions hidden. The BCNC portal A1C upload slots stay available.
 */
export function isPayerHiddenAttachment(question: { type?: string; linkId?: string } | null | undefined): boolean {
  return question?.type === "attachment" && !PORTAL_A1C_IMAGE_UPLOAD_LINK_IDS.includes(question.linkId ?? "");
}

/**
 * Depth-first search of a QuestionnaireResponse item tree for answers by `linkId`.
 *
 * @remarks
 * Handles both flat (new submits) and nested (legacy or group-wrapped) response structures.
 *
 * @example
 * ```typescript
 * findQRAnswerByLinkId(qr.item, "PAYOR-A1C-IMG-ATTACHMENT");
 * // => [{ valueReference: { reference: "DocumentReference/abc" } }]
 * ```
 */
export function findQRAnswerByLinkId(items: any[] | undefined, linkId: string): any[] | null {
  for (const node of items ?? []) {
    if (node?.linkId === linkId && Array.isArray(node?.answer) && node.answer.length > 0) {
      return node.answer;
    }
    const nested = findQRAnswerByLinkId(node?.item, linkId);
    if (nested) return nested;
  }
  return null;
}

/**
 * Collects every `valueReference` from any depth of a QuestionnaireResponse item tree.
 *
 * @remarks
 * Needed because saved A1C images may be stored inside a group node
 * (`PAYOR-A1C-IMG-UPLOAD-GROUP`), so a two-level walk misses them.
 */
export function collectQRValueReferences(items: any[] | undefined): any[] {
  const refs: any[] = [];
  for (const node of items ?? []) {
    const answers: any[] = node?.answer ?? [];
    const last = answers[answers.length - 1];
    if (last?.valueReference) {
      refs.push(last.valueReference);
    }
    refs.push(...collectQRValueReferences(node?.item));
  }
  return refs;
}

/**
 * Converts a base-64 encoded PDF data URL to a `Blob` for use as an object URL.
 */
export function base64ToPdfBlob(dataUrl: string): Blob {
  const base64 = dataUrl.replace(/^data:application\/pdf;base64,/, "").replace(/\s/g, "");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "application/pdf" });
}
